import config from '@frolight/config';
import HTTPStatusError from '@frolight/utils/HTTPStatusError';

const ROOT_URL = `${config.api.uri}/gateways`;

const OVERVIEW_URL = `${ROOT_URL}/`;
const UNREGISTERED_OVERVIEW_URL = `${ROOT_URL}/unregistered`;
const DETAIL_URL = `${ROOT_URL}/:gatewayId`;
const REGISTER_URL = `${ROOT_URL}/:gatewayId/register`;
const REGISTRATION_CODE_URL = `${ROOT_URL}/registration-code/:registrationCode`
const CONNECTED_UNITS_URL = `${ROOT_URL}/:gatewayId/connected-units`;
const CONNECTED_PERIPHERALS_URL = `${ROOT_URL}/:gatewayId/connected-units/:connectedUnitDeviceId/peripherals`;

const UNIT_SETTINGS_MESSAGES_URL = `${ROOT_URL}/:gatewayId/connected-units/:connectedUnitDeviceId/messages/settings`;
const PERIPHERAL_STATUS_MESSAGES_URL = `${ROOT_URL}/:gatewayId/connected-units/:connectedUnitDeviceId/peripherals/:peripheralDeviceId/messages/status`;
const PERIPHERAL_TELEMETRY_MESSAGES_URL = `${ROOT_URL}/:gatewayId/connected-units/:connectedUnitDeviceId/peripherals/:peripheralDeviceId/messages/telemetry`;

export const getGateways = async (abortController, accessToken, { filters } = { filters: {} }) => {
  try {
    const url = new URL(OVERVIEW_URL);
    for (const key in filters) {
      if (Array.isArray(filters[key])) {
        filters[key].forEach(value => url.searchParams.append(key, value));
      } else {
        url.searchParams.append(key, filters[key]);
      }
    }

    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Accept': 'application/json',
        'Authorization': `${accessToken.type} ${accessToken.token}`
      },
      signal: abortController.signal
    });

    if (!response.ok) {
      throw new HTTPStatusError(response.status, response.statusText);
    }

    const data = await response.json();
    return data;
  } catch (err) {
    if (err.name !== 'AbortError') {
      throw err;
    }
  }
};

export const getUnregisteredGateways = async (abortController, { gatewayIds }) => {
  try {
    const urlParams = new URLSearchParams();
    gatewayIds.forEach(id => {
      urlParams.append('id', id);
    });

    const response = await fetch(`${UNREGISTERED_OVERVIEW_URL}?${urlParams}`, {
      method: 'GET',
      headers: {
        'Accept': 'application/json'
      },
      signal: abortController.signal
    });

    if (!response.ok) {
      throw new HTTPStatusError(response.status, response.statusText);
    }

    const data = await response.json();
    return data;
  } catch (err) {
    if (err.name !== 'AbortError') {
      throw err;
    }
  }
};

export const registerGatewayToOrganisation = async (abortController, accessToken, { gatewayId, organisationId }) => {
  try {
    const response = await fetch(REGISTER_URL.replace(':gatewayId', gatewayId), {
      method: 'PATCH',
      headers: {
        'Accept': 'application/json',
        'Authorization': `${accessToken.type} ${accessToken.token}`,
        'Content-Type': 'application/json; charset=UTF-8'
      },
      body: JSON.stringify({
        organisationId: organisationId
      }),
      signal: abortController.signal
    });

    if (!response.ok) {
      throw new HTTPStatusError(response.status, response.statusText);
    }

    const data = await response.json();
    return data;
  } catch (err) {
    if (err.name !== 'AbortError') {
      throw err;
    }
  }
};

export const getGateway = async (abortController, accessToken, { gatewayId }) => {
  try {
    const response = await fetch(DETAIL_URL.replace(':gatewayId', gatewayId), {
      method: 'GET',
      headers: {
        'Accept': 'application/json',
        'Authorization': `${accessToken.type} ${accessToken.token}`
      },
      signal: abortController.signal
    });

    if (!response.ok) {
      throw new HTTPStatusError(response.status, response.statusText);
    }

    const data = await response.json();
    return data;
  } catch (err) {
    if (err.name !== 'AbortError') {
      throw err;
    }
  }
};

export const getGatewayByRegistrationCode = async (abortController, { registrationCode }) => {
  try {
    const response = await fetch(REGISTRATION_CODE_URL.replace(':registrationCode', registrationCode), {
      method: 'GET',
      headers: {
        'Accept': 'application/json'
      },
      signal: abortController.signal
    });

    if (!response.ok) {
      throw new HTTPStatusError(response.status, response.statusText);
    }

    const data = await response.json();
    return data;
  } catch (err) {
    if (err.name !== 'AbortError') {
      throw err;
    }
  }
};

export const getSettingsMessagesForUnit = async (abortController, accessToken, { gatewayId, unitDeviceId, filters }) => {
  try {
    const url = new URL(
      UNIT_SETTINGS_MESSAGES_URL
        .replace(':gatewayId', gatewayId)
        .replace(':connectedUnitDeviceId', unitDeviceId)
    );
    for (const key in filters) {
      url.searchParams.append(key, filters[key]);
    }

    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Accept': 'application/json',
        'Authorization': `${accessToken.type} ${accessToken.token}`
      },
      signal: abortController.signal
    });
    if (!response.ok) {
      throw new HTTPStatusError(response.status, response.statusText);
    }
    const data = await response.json();
    return data;
  } catch (err) {
    if (err.name !== 'AbortError') {
      throw err;
    }
  }
};

export const getStatusMessagesForPeripheral = async (abortController, accessToken, { gatewayId, unitDeviceId, peripheralDeviceId, filters }) => {
  try {
    const url = new URL(
      PERIPHERAL_STATUS_MESSAGES_URL
        .replace(':gatewayId', gatewayId)
        .replace(':connectedUnitDeviceId', unitDeviceId)
        .replace(':peripheralDeviceId', peripheralDeviceId)
    );
    for (const key in filters) {
      url.searchParams.append(key, filters[key]);
    }

    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Accept': 'application/json',
        'Authorization': `${accessToken.type} ${accessToken.token}`
      },
      signal: abortController.signal
    });
    if (!response.ok) {
      throw new HTTPStatusError(response.status, response.statusText);
    }
    const data = await response.json();
    return data;
  } catch (err) {
    if (err.name !== 'AbortError') {
      throw err;
    }
  }
};

export const getTelemetryMessagesForPeripheral = async (abortController, accessToken, { gatewayId, unitDeviceId, peripheralDeviceId, filters }) => {
  try {
    const url = new URL(
      PERIPHERAL_TELEMETRY_MESSAGES_URL
        .replace(':gatewayId', gatewayId)
        .replace(':connectedUnitDeviceId', unitDeviceId)
        .replace(':peripheralDeviceId', peripheralDeviceId)
    );
    for (const key in filters) {
      url.searchParams.append(key, filters[key]);
    }

    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Accept': 'application/json',
        'Authorization': `${accessToken.type} ${accessToken.token}`
      },
      signal: abortController.signal
    });
    if (!response.ok) {
      throw new HTTPStatusError(response.status, response.statusText);
    }
    const data = await response.json();
    return data;
  } catch (err) {
    if (err.name !== 'AbortError') {
      throw err;
    }
  }
};

export const getUnitsConnectedToGateway = async(abortController, accessToken, { gatewayId }) => {
  try {
    const url = new URL(CONNECTED_UNITS_URL.replace(':gatewayId', gatewayId));

    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Accept': 'application/json',
        'Authorization': `${accessToken.type} ${accessToken.token}`
      },
      signal: abortController.signal
    });

    if (!response.ok) {
      throw new HTTPStatusError(response.status, response.statusText);
    }

    const data = await response.json();
    return data;
  } catch (err) {
    if (err.name !== 'AbortError') {
      throw err;
    }
  }
};

export const getPeripheralsConnectedToUnitAndGateway = async (abortController, accessToken, { gatewayId, unitDeviceId }) => {
  try {
    const url = new URL(CONNECTED_PERIPHERALS_URL
      .replace(':gatewayId', gatewayId)
      .replace(':connectedUnitDeviceId', unitDeviceId)
    );

    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Accept': 'application/json',
        'Authorization': `${accessToken.type} ${accessToken.token}`
      },
      signal: abortController.signal
    });

    if (!response.ok) {
      throw new HTTPStatusError(response.status, response.statusText);
    }

    const data = await response.json();
    return data;
  } catch (err) {
    if (err.name !== 'AbortError') {
      throw err;
    }
  }
};
